import { priorityPartners, partners, realEstatePartners } from "@/mocks/homeData";
import { useTranslation } from "react-i18next";
import { Link } from "react-router-dom";

export default function PartnersShowcase() {
  const { t } = useTranslation();

  return (
    <section className="relative bg-background-950 py-14 md:py-20 overflow-hidden">
      {/* Subtle decorative background blobs */}
      <div className="absolute top-1/3 left-0 w-72 md:w-96 h-72 md:h-96 bg-primary-500/3 rounded-full blur-3xl pointer-events-none"></div>
      <div className="absolute bottom-1/4 right-0 w-80 md:w-[30rem] h-80 md:h-[30rem] bg-secondary-500/3 rounded-full blur-3xl pointer-events-none"></div>

      <div className="relative px-4 md:px-6 max-w-6xl mx-auto">
        <div className="text-center mb-10 md:mb-14">
          <span className="inline-block px-4 py-1.5 border border-secondary-400/30 text-secondary-300 text-xs md:text-sm font-medium rounded-full whitespace-nowrap">
            {t("partners.badge")}
          </span>
          <h2 className="font-heading text-2xl md:text-4xl lg:text-5xl font-bold text-background-50 mt-4">
            {t("partners.heading")}
          </h2>
          <p className="text-sm md:text-base text-background-50/60 mt-3 max-w-lg mx-auto">
            {t("partners.description")}
          </p>
        </div>

        {/* Priority Key & Cylinder Partners */}
        <div className="mb-12 md:mb-16">
          <div className="flex items-center gap-4 mb-6">
            <div className="flex-1 h-px bg-gradient-to-r from-transparent via-[#ffcc00] to-transparent"></div>
            <span className="text-xs md:text-sm text-background-50/40 font-medium tracking-wide uppercase whitespace-nowrap">{t("partners.keyPartners")}</span>
            <div className="flex-1 h-px bg-gradient-to-r from-transparent via-[#ffcc00] to-transparent"></div>
          </div>
          <div className="w-fit max-w-full mx-auto flex flex-wrap justify-center gap-3 md:gap-4">
            {priorityPartners.map((partner, index) => {
              const isDarkCard = (partner as { darkCard?: boolean }).darkCard;
              const isBoost = (partner as { boost?: boolean }).boost;
              const isBoostXl = (partner as { boostXl?: boolean }).boostXl;
              return (
              <a
                key={index}
                href={partner.website}
                target="_blank"
                rel="noopener noreferrer"
                className={`rounded-2xl border overflow-hidden group transition-all duration-300 flex flex-col items-center hover:border-primary-500/60 cursor-pointer flex-none w-36 sm:w-40 lg:w-44 ${
                  isDarkCard
                    ? "bg-background-900 border-background-700"
                    : "bg-background-200 border-background-300/60"
                }`}
              >
                <div className={`flex-1 w-full flex items-center justify-center min-h-[88px] md:min-h-[104px] ${
                  isBoostXl ? "p-1.5 md:p-2" : "p-4 md:p-5"
                }`}>
                  <img
                    src={partner.logo}
                    alt={partner.name}
                    className={`max-w-full object-contain transition-transform duration-400 group-hover:scale-105 ${
                      isBoostXl ? "h-24 md:h-28 w-full" : isBoost ? "h-16 md:h-20 w-auto" : "h-11 md:h-14 w-auto"
                    }`}
                    title={partner.name}
                  />
                </div>
                <div className="w-full px-3 pb-3 pt-1 text-center">
                  <span className={`font-heading text-[11px] md:text-xs font-semibold group-hover:text-primary-600 transition-colors leading-tight block ${
                    isDarkCard ? "text-background-50" : "text-foreground-800"
                  }`}>
                    {partner.name}
                  </span>
                </div>
              </a>
              );
            })}
          </div>
        </div>

        {/* Divider */}
        <div className="flex items-center gap-4 mb-12 md:mb-16">
          <div className="flex-1 h-px bg-gradient-to-r from-transparent via-[#ffcc00] to-transparent"></div>
          <span className="text-xs md:text-sm text-background-50/40 font-medium tracking-wide uppercase whitespace-nowrap">{t("partners.allPartners")}</span>
          <div className="flex-1 h-px bg-gradient-to-r from-transparent via-[#ffcc00] to-transparent"></div>
        </div>

        {/* All Suppliers */}
        <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-3 md:gap-4">
          {partners.map((partner, index) => {
            const isDarkCard = (partner as { darkCard?: boolean }).darkCard;
            const isBoost = (partner as { boost?: boolean }).boost;
            const isBoostXl = (partner as { boostXl?: boolean }).boostXl;
            return (
            <a
              key={index}
              href={partner.website}
              target="_blank"
              rel="noopener noreferrer"
              className={`rounded-2xl border overflow-hidden group transition-all duration-300 flex flex-col items-center hover:border-primary-500/60 cursor-pointer ${
                isDarkCard
                  ? "bg-background-900 border-background-700"
                  : "bg-background-200 border-background-300/60"
              }`}
            >
              <div className="flex-1 w-full flex items-center justify-center p-4 md:p-5 min-h-[88px] md:min-h-[104px]">
                <img
                  src={partner.logo}
                  alt={partner.name}
                  className={`w-auto max-w-full object-contain transition-transform duration-400 group-hover:scale-105 ${
                    isBoostXl ? "h-24 md:h-28" : isBoost ? "h-16 md:h-20" : "h-11 md:h-14"
                  }`}
                  onError={(e) => {
                    const target = e.currentTarget as HTMLImageElement;
                    target.style.display = 'none';
                    const fallback = target.nextElementSibling as HTMLElement;
                    if (fallback) fallback.style.display = 'flex';
                  }}
                />
                <div style={{display: 'none'}} className="w-full h-12 md:h-14 items-center justify-center">
                  <i className="ri-building-2-line text-background-400 text-3xl"></i>
                </div>
              </div>
              <div className="w-full px-3 pb-3 pt-1 text-center">
                <span className={`font-heading text-[11px] md:text-xs font-semibold group-hover:text-primary-600 transition-colors leading-tight block ${
                  isDarkCard ? "text-background-50" : "text-foreground-800"
                }`}>
                  {partner.name}
                </span>
                {partner.note && (
                  <span className={`text-[10px] mt-1 font-medium whitespace-nowrap block ${
                    isDarkCard ? "text-background-50/60" : "text-foreground-500"
                  }`}>
                    {partner.note}
                  </span>
                )}
              </div>
            </a>
          );
          })}
        </div>

        {/* Divider */}
        <div className="flex items-center gap-4 mt-16 md:mt-20 mb-10 md:mb-12">
          <div className="flex-1 h-px bg-gradient-to-r from-transparent via-[#ffcc00] to-transparent"></div>
          <span className="text-xs md:text-sm text-background-50/40 font-medium tracking-wide uppercase whitespace-nowrap">{t("partners.realEstate")}</span>
          <div className="flex-1 h-px bg-gradient-to-r from-transparent via-[#ffcc00] to-transparent"></div>
        </div>

        {/* Real Estate Partners */}
        <div>
          <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-5 gap-3 md:gap-4">
            {realEstatePartners.slice(0, 9).map((partner, index) => {
              const isDarkCard = (partner as { darkCard?: boolean }).darkCard;
              const isBoost = (partner as { boost?: boolean }).boost;
              const isBoostXl = (partner as { boostXl?: boolean }).boostXl;
              return (
              <a
                key={index}
                href={partner.website}
                target="_blank"
                rel="noopener noreferrer"
                className={`rounded-2xl border overflow-hidden group transition-all duration-300 flex flex-col items-center hover:border-secondary-500/60 ${
                  isDarkCard
                    ? "bg-background-900 border-background-700"
                    : "bg-background-200 border-background-300/60"
                }`}
              >
                <div className={`flex-1 w-full flex items-center justify-center min-h-[88px] md:min-h-[104px] ${
                  isBoostXl ? "p-2 md:p-3" : "p-4 md:p-5"
                }`}>
                  {partner.logo ? (
                    <img
                      src={partner.logo}
                      alt={partner.name}
                      className={`w-auto max-w-full object-contain transition-transform duration-400 group-hover:scale-105 ${
                        isBoostXl ? "h-28 md:h-32" : isBoost ? "h-16 md:h-20" : "h-11 md:h-14"
                      }`}
                    />
                  ) : (
                    <div className="w-11 h-11 md:w-12 md:h-12 rounded-xl bg-secondary-500/10 flex items-center justify-center transition-transform duration-400 group-hover:scale-105">
                      <i className="ri-home-4-line text-secondary-600 text-xl md:text-2xl"></i>
                    </div>
                  )}
                </div>
                <div className="w-full px-3 pb-3 pt-1 text-center">
                  <span className={`font-heading text-[11px] md:text-xs font-semibold transition-colors leading-tight block group-hover:text-secondary-600 ${
                    isDarkCard ? "text-background-50" : "text-foreground-800"
                  }`}>
                    {partner.name}
                  </span>
                </div>
              </a>
              );
            })}
            {/* "+" More Partners Card */}
            <Link
              to="/partners"
              className="rounded-2xl border border-dashed border-secondary-400/30 bg-transparent overflow-hidden group transition-all duration-300 flex flex-col items-center justify-center hover:border-secondary-500/60 hover:bg-background-200/5 cursor-pointer min-h-[88px] md:min-h-[104px]"
            >
              <div className="w-12 h-12 md:w-14 md:h-14 rounded-full bg-secondary-500/10 flex items-center justify-center transition-all duration-300 group-hover:bg-secondary-500/20 group-hover:scale-110">
                <i className="ri-add-line text-secondary-500 text-2xl md:text-3xl"></i>
              </div>
              <span className="text-[11px] md:text-xs font-medium text-secondary-400 mt-2 group-hover:text-secondary-300 transition-colors">
                {t("partners.viewAll")}
              </span>
            </Link>
          </div>
        </div>
      </div>
    </section>
  );
}